import { useEffect, useRef, useState } from 'react'

import styles from './TableHeaderWithTooltip.module.scss'
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useRole,
    arrow,
} from '@floating-ui/react'

const useTextOverflow = () => {
    const ref = useRef<HTMLElement>(null)
    const [isOverflowing, setIsOverflowing] = useState(false)

    useEffect(() => {
        const element = ref.current
        if (!element) return

        const checkOverflow = () => {
            setIsOverflowing(element.scrollHeight > element.clientHeight)
        }

        // Initial check
        checkOverflow()

        // Watch for size changes
        const resizeObserver = new ResizeObserver(checkOverflow)
        resizeObserver.observe(element)

        // Also watch for content changes with MutationObserver
        const mutationObserver = new MutationObserver(checkOverflow)
        mutationObserver.observe(element, {
            childList: true,
            subtree: true,
            characterData: true,
        })

        return () => {
            resizeObserver.disconnect()
            mutationObserver.disconnect()
        }
    }, [])

    return { ref, isOverflowing }
}

export const TableHeaderWithTooltip = ({ children, title }: { children: React.ReactNode; title?: string }) => {
    const { ref, isOverflowing } = useTextOverflow()
    const [isOpen, setIsOpen] = useState(false)
    const arrowRef = useRef<HTMLDivElement>(null)

    const { refs, floatingStyles, context, placement, middlewareData } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'top',
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(10),
            flip(),
            shift(),
            arrow({
                element: arrowRef,
                padding: 4,
            }),
        ],
    })

    const hover = useHover(context)
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role])

    // Calculate arrow position
    const { x: arrowX, y: arrowY } = middlewareData.arrow || {}

    const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
    }[placement.split('-')[0]]

    return (
        <>
            <span ref={refs.setReference} {...getReferenceProps()}>
                <span ref={ref} className={styles.smallCell}>
                    {children}
                </span>
            </span>
            {isOverflowing && isOpen && (
                <FloatingPortal>
                    <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()} className="tooltip">
                        <div className={styles.tooltip}>{title}</div>
                        <div
                            ref={arrowRef}
                            className={styles.tooltipArrow}
                            style={{
                                left: arrowX != null ? `${arrowX}px` : '',
                                top: arrowY != null ? `${arrowY}px` : '',
                                right: '',
                                bottom: '',
                                [staticSide as string]: '-3px',
                            }}
                        />
                    </div>
                </FloatingPortal>
            )}
        </>
    )
}
